import React, { useMemo, useState } from 'react';
import { DataGridPro, GridCellEditStopReasons } from '@mui/x-data-grid-pro';
import TableToolbar from '../tableToolbar';
import { formatDateMMDDYY } from '../../utilities/dateHelpers';
import { CommentFieldCell, CommentFieldEdit } from './commentField.js';
import { datagridsx } from '../../constants/datagridsx.js';

function dateFormatter(params) {
    return params ? formatDateMMDDYY(new Date(params)) : '';
}

const ColumnDefinitions = [
    {
        field: 'Project Name',
        headerName: 'Project Name',
        type: 'text',
    },
    {
        field: 'Serial Number',
        headerName: 'Serial Number',
        type: 'text',
    },
    {
        field: 'City',
        headerName: 'City',
        type: 'text',
    },
    {
        field: 'NTA Version',
        headerName: 'NTA Version',
        type: 'text',
    },
    {
        field: 'Comments',
        headerName: 'Comments',
        type: 'text',
        editable: true,
        width: 200,
        renderCell: (params) => <CommentFieldCell {...params} />,
        renderEditCell: (params) => <CommentFieldEdit {...params} />,
    },
    {
        field: 'Design Release',
        headerName: 'Shops / Kits /FIDs / Subs',
        type: 'date',
    },
    {
        field: 'Manufacturing Start',
        headerName: 'Mfg Start',
        type: 'date',
    },
    {
        field: 'Ship Mods',
        headerName: 'Ship Mods',
        type: 'date',
    },
    {
        field: 'Ship Field Kit 1',
        headerName: 'Ship Field Kit 1',
        type: 'date',
    },
    {
        field: 'Ship Field Kit 2',
        headerName: 'Ship Field Kit 2',
        type: 'date',
    },
    {
        field: 'Ship Field Kit 3',
        headerName: 'Ship Field Kit 3',
        type: 'date',
    },
    {
        field: 'Ship Field Kit 4',
        headerName: 'Ship Field Kit 4',
        type: 'date',
    },
    {
        field: 'Material Return',
        headerName: 'Material Return',
        type: 'date',
    },
];
const ManufacturingTable = ({ projects, onUpdate }) => {
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        id: false,
    });

    const columns = useMemo(() => {
        let cols = [];
        if (projects) {
            cols = ColumnDefinitions.map((c) =>
                c.type === 'date'
                    ? { ...c, valueFormatter: dateFormatter, width: 125 }
                    : { ...c }
            );

            const supplementalDeliveryCount = [
                ...new Set(projects.flatMap(Object.keys)),
            ].filter((c) => c.includes('Supplemental Delivery')).length;
            for (let i = 1; i <= supplementalDeliveryCount; ++i) {
                cols.push({
                    field: `Supplemental Delivery ${i}`,
                    headerName: `Supplemental Delivery ${i}`,
                    type: 'text',
                });
            }
        }
        return cols;
    }, [projects]);

    const tableRows = useMemo(
        () =>
            projects
                .map((p, index) => {
                    const row = { ...p, id: index };
                    for (let f in row) {
                        const col = ColumnDefinitions.find((c) => c.field === f);
                        if (col && col.type === 'date' && row[f]) {
                            row[f] = new Date(row[f]);
                        }
                    }
                    return row;
                })
                .sort(
                    (a, b) =>
                        new Date(b['Manufacturing Start']) -
            new Date(a['Manufacturing Start'])
                ),
        [projects]
    );

    const handleProcessRowUpdate = (newRow, oldRow) => {
        if (newRow?.Comments != oldRow?.Comments) {
            onUpdate(newRow['Project ID'], 'comments', newRow.Comments);
        }
        return newRow;
    };

    const handleProcessRowUpdateError = (error) => {
        console.log('DG row update error', { error });
    };

    return (
        <DataGridPro
            rows={tableRows}
            columns={columns}
            slots={{
                toolbar: TableToolbar,
            }}
            slotProps={{
                pagination: {
                    showFirstButton: true,
                    showLastButton: true,
                },
                toolbar: { fileName: 'manufacturing' },
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
            }
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onCellEditStop={(params, event) => {
                if (
                    [
                        GridCellEditStopReasons.cellFocusOut,
                        GridCellEditStopReasons.tabKeyDown,
                        GridCellEditStopReasons.enterKeyDown,
                    ].includes(params.reason)
                ) {
                    event.defaultMuiPrevented = true;
                }
            }}
            getRowHeight={() => 'auto'}
            sx={{
                ...datagridsx,
            }}
        />
    );
};

export default ManufacturingTable;
